import type { FieldErrors, Issue } from '../../lib/validate';
import {
  STAGE_LOCATION_TYPES,
  STAGE_NEAR,
  TICKET_KINDS,
  type MissionStageDef,
  type StageLocationDef,
} from '../../lib/validate/scenarios';
import {
  Fieldset,
  IssueList,
  ListEditor,
  NumberField,
  SelectField,
  StringListEditor,
  TextField,
  smallBtn,
} from './fields';
import { useContentBundle, type Option } from './refs';

const opts = (list: readonly string[]): Option[] => list.map((v) => ({ value: v, label: v }));

const newStage = (n: number): MissionStageDef => ({
  id: `stage${n}`,
  title: '',
  location: { type: 'building', categories: [], kinds: [], near: 'player', maxDistanceM: 1500 },
  encounter: { kind: 'interior', scenarioId: '', beatId: '' },
  onLose: 'retry',
  next: '',
});

/** A location rule with only the fields its type reads (building: categories, kinds, near, distance; same_building: ref; trigger_code). */
function locationFor(type: string, prev: StageLocationDef): StageLocationDef {
  if (type === 'building')
    return {
      type,
      categories: prev.categories ?? [],
      kinds: prev.kinds ?? [],
      near: prev.near || 'player',
      maxDistanceM: prev.maxDistanceM ?? 1500,
    };
  if (type === 'same_building') return { type, ref: prev.ref ?? '' };
  if (type === 'trigger_code') return { type, triggerId: prev.triggerId ?? '' };
  return { type };
}

/**
 * A mission's stages (scenario.html §3 "Multi-stage missions", `missions[].stages`): cards with the location rule, the encounter,
 * onLose (retry, fail or a branch) and next, plus a small graph (boxes in order, `next` arrows solid, onLose branches dashed red).
 * No stages = a single-win mission (ADR 0029). Issues are the mission's, keyed `stages.N.…`.
 */
export function MissionStagesEditor({
  value,
  onChange,
  errors,
}: {
  value: MissionStageDef[];
  onChange: (next: MissionStageDef[]) => void;
  errors: FieldErrors;
}) {
  const b = useContentBundle();
  const ids = value.map((s) => s?.id ?? '').filter(Boolean);
  const scenarios = (b.scenarios ?? [])
    .filter((s) => s != null && s.id)
    .map((s) => ({
      value: s!.id!,
      label: `${s!.title || s!.id} (${s!.id}, ${s!.kind || 'interior'})`,
    }));
  const beats = (b.beatChains ?? [])
    .filter((c) => c != null && c.id)
    .map((c) => ({ value: c!.id!, label: `${c!.title || c!.id} (${c!.id})` }));
  const triggers = (b.triggers ?? [])
    .filter((t) => t != null && t.id)
    .map((t) => ({ value: t!.id!, label: `${t!.title || t!.id} (${t!.id})` }));
  const categories = (b.categories ?? []).map((c) => ({ value: c.id ?? '', label: c.id ?? '' }));
  const under = (base: string): Issue[] =>
    Object.entries(errors)
      .filter(([k]) => k === base || k.startsWith(`${base}.`))
      .flatMap(([, v]) => v);

  return (
    <Fieldset legend="Stages (multi-stage mission)">
      <p className="text-xs text-bruise sm:col-span-2">
        No stages: the mission completes on one win of its trigger. With stages, the trigger is
        optional and the mission completes on its last stage (1..6 stages).
      </p>
      {value.length > 0 && <StageGraph stages={value} />}
      <ListEditor
        label="Stages"
        what="stage"
        items={value}
        issues={errors.stages}
        make={() => newStage(value.length + 1)}
        onChange={onChange}
        row={(st, i, setRow) => {
          const at = (f: string) => errors[`stages.${i}.${f}`];
          const loc = st.location ?? { type: 'building' };
          const enc = st.encounter ?? { kind: 'interior' };
          const setLoc = (patch: Partial<StageLocationDef>) =>
            setRow({ ...st, location: { ...loc, ...patch } });
          const setEnc = (patch: Partial<NonNullable<MissionStageDef['encounter']>>) =>
            setRow({ ...st, encounter: { ...enc, ...patch } });
          const others = ids.filter((x) => x !== st.id).map((x) => ({ value: x, label: x }));
          const earlier = value
            .slice(0, i)
            .map((s) => s?.id ?? '')
            .filter(Boolean)
            .map((x) => ({ value: x, label: x }));
          const kind = enc.kind || 'interior';
          const onLose = st.onLose || 'retry';
          return (
            <div className="rounded border-2 border-ink p-2">
              <div className="grid gap-2 sm:grid-cols-2">
                <TextField
                  label={`Stage ${i + 1} id`}
                  mono
                  value={st.id}
                  issues={at('id')}
                  onChange={(v) => setRow({ ...st, id: v })}
                />
                <TextField
                  label="Stage title"
                  value={st.title}
                  issues={at('title')}
                  onChange={(v) => setRow({ ...st, title: v })}
                />
                <SelectField
                  label="Location"
                  value={loc.type || 'building'}
                  options={opts(STAGE_LOCATION_TYPES)}
                  hint="building/same_building/tip_target pin a target (tip_target: the building a story tip flagged); trigger_code waits for a scan; home/camp/base/anywhere play in place."
                  issues={at('location.type')}
                  onChange={(v) => setRow({ ...st, location: locationFor(v ?? 'building', loc) })}
                />
                {(loc.type || 'building') === 'building' && (
                  <>
                    <SelectField
                      label="Near"
                      value={loc.near || 'player'}
                      options={opts(STAGE_NEAR)}
                      issues={at('location.near')}
                      onChange={(v) => setLoc({ near: v ?? 'player' })}
                    />
                    <NumberField
                      label="Max distance (m)"
                      min={0}
                      max={5000}
                      value={loc.maxDistanceM ?? 0}
                      hint="0 = 1500 m (doubled in sparse areas)."
                      issues={at('location.maxDistanceM')}
                      onChange={(v) => setLoc({ maxDistanceM: v ?? 0 })}
                    />
                    <div className="sm:col-span-2">
                      <span className="block text-xs font-semibold uppercase tracking-wide">
                        Categories
                      </span>
                      <div className="flex flex-wrap gap-1">
                        {categories.map((c) => {
                          const on = (loc.categories ?? []).includes(c.value);
                          return (
                            <button
                              key={c.value}
                              type="button"
                              aria-pressed={on}
                              className={`${smallBtn} ${on ? 'bg-nonphoto' : ''}`}
                              onClick={() =>
                                setLoc({
                                  categories: on
                                    ? (loc.categories ?? []).filter((x) => x !== c.value)
                                    : [...(loc.categories ?? []), c.value],
                                })
                              }
                            >
                              {c.label}
                            </button>
                          );
                        })}
                      </div>
                      <IssueList issues={under(`stages.${i}.location.categories`)} />
                    </div>
                    <StringListEditor
                      label="Building kinds"
                      value={loc.kinds}
                      errors={errors}
                      base={`stages.${i}.location.kinds`}
                      placeholder="hospital"
                      hint="buildings.kind values; none = any."
                      onChange={(v) => setLoc({ kinds: v })}
                    />
                  </>
                )}
                {loc.type === 'same_building' && (
                  <SelectField
                    label="Back to stage"
                    value={loc.ref ?? ''}
                    options={earlier}
                    issues={at('location.ref')}
                    onChange={(v) => setLoc({ ref: v ?? '' })}
                  />
                )}
                {loc.type === 'trigger_code' && (
                  <SelectField
                    label="Scan trigger"
                    value={loc.triggerId ?? ''}
                    options={triggers}
                    issues={at('location.triggerId')}
                    onChange={(v) => setLoc({ triggerId: v ?? '' })}
                  />
                )}
                <SelectField
                  label="Encounter kind"
                  value={kind}
                  options={opts(TICKET_KINDS)}
                  hint="interior and comic play now; arena and overwatch need Step 3."
                  issues={at('encounter.kind')}
                  onChange={(v) => setEnc({ kind: v ?? 'interior' })}
                />
                <SelectField
                  label="Scenario"
                  value={enc.scenarioId ?? ''}
                  none={kind === 'comic' ? '(none: play a beat)' : undefined}
                  options={scenarios}
                  issues={at('encounter.scenarioId')}
                  onChange={(v) => setEnc({ scenarioId: v ?? '' })}
                />
                {kind === 'comic' && (
                  <SelectField
                    label="Beat chain"
                    value={enc.beatId ?? ''}
                    none="(none: play a comic scenario)"
                    options={beats}
                    issues={at('encounter.beatId')}
                    onChange={(v) => setEnc({ beatId: v ?? '' })}
                  />
                )}
                <SelectField
                  label="On lose"
                  value={onLose}
                  options={[
                    { value: 'retry', label: 'retry (same target)' },
                    { value: 'fail', label: 'fail the mission' },
                    ...others.map((o) => ({ value: o.value, label: `branch to ${o.label}` })),
                  ]}
                  issues={at('onLose')}
                  onChange={(v) => setRow({ ...st, onLose: v ?? 'retry' })}
                />
                <SelectField
                  label="Next"
                  value={st.next ?? ''}
                  none="(last stage)"
                  options={others}
                  issues={at('next')}
                  onChange={(v) => setRow({ ...st, next: v ?? '' })}
                />
              </div>
              <IssueList issues={errors[`stages.${i}`]} />
              <IssueList issues={at('encounter')} />
            </div>
          );
        }}
      />
    </Fieldset>
  );
}

/** Boxes left to right in list order; `next` arrows solid, onLose branches dashed red (retry and fail aren't drawn). */
function StageGraph({ stages }: { stages: MissionStageDef[] }) {
  const W = 120;
  const H = 40;
  const GAP = 40;
  const list = stages.map((s, i) => ({ s, i })).filter(({ s }) => s != null);
  const x = (i: number) => 10 + i * (W + GAP);
  const index = (id: string | null | undefined) => stages.findIndex((s) => s?.id === id && !!id);
  const width = 20 + stages.length * (W + GAP) - GAP;
  const arc = (from: number, to: number, below: boolean) => {
    const a = x(from) + W / 2;
    const c = x(to) + W / 2;
    const y0 = below ? 30 + H : 30;
    const lift = (below ? 1 : -1) * (18 + 6 * Math.abs(to - from));
    return `M ${a} ${y0} C ${a} ${y0 + lift}, ${c} ${y0 + lift}, ${c} ${y0}`;
  };
  return (
    <div className="overflow-x-auto sm:col-span-2">
      <svg
        role="img"
        aria-label="Stage graph"
        width={width}
        height={H + 70}
        viewBox={`0 0 ${width} ${H + 70}`}
        className="text-ink"
      >
        <defs>
          <marker
            id="stage-arrow"
            viewBox="0 0 10 10"
            refX="9"
            refY="5"
            markerWidth="6"
            markerHeight="6"
            orient="auto"
          >
            <path d="M0,0 L10,5 L0,10 z" fill="var(--color-ink)" />
          </marker>
          <marker
            id="stage-arrow-red"
            viewBox="0 0 10 10"
            refX="9"
            refY="5"
            markerWidth="6"
            markerHeight="6"
            orient="auto"
          >
            <path d="M0,0 L10,5 L0,10 z" fill="var(--color-blam)" />
          </marker>
        </defs>
        {list.map(({ s, i }) => {
          const n = index(s.next);
          if (n < 0) return null;
          const adjacent = n === i + 1;
          return adjacent ? (
            <line
              key={`n${i}`}
              data-edge="next"
              x1={x(i) + W}
              y1={30 + H / 2}
              x2={x(n) - 2}
              y2={30 + H / 2}
              stroke="var(--color-ink)"
              strokeWidth={2}
              markerEnd="url(#stage-arrow)"
            />
          ) : (
            <path
              key={`n${i}`}
              data-edge="next"
              d={arc(i, n, false)}
              fill="none"
              stroke="var(--color-ink)"
              strokeWidth={2}
              markerEnd="url(#stage-arrow)"
            />
          );
        })}
        {list.map(({ s, i }) => {
          const n = index(s.onLose);
          if (n < 0) return null;
          return (
            <path
              key={`l${i}`}
              data-edge="onLose"
              d={arc(i, n, true)}
              fill="none"
              stroke="var(--color-blam)"
              strokeWidth={2}
              strokeDasharray="5 4"
              markerEnd="url(#stage-arrow-red)"
            />
          );
        })}
        {list.map(({ s, i }) => (
          <g key={`b${i}`}>
            <rect
              x={x(i)}
              y={30}
              width={W}
              height={H}
              rx={4}
              fill="white"
              stroke="var(--color-ink)"
              strokeWidth={2}
            />
            <text x={x(i) + W / 2} y={30 + 16} fontSize={11} fontWeight="bold" textAnchor="middle">
              {i + 1}. {(s.id ?? '').slice(0, 14)}
            </text>
            <text x={x(i) + W / 2} y={30 + 31} fontSize={10} textAnchor="middle">
              {(s.location?.type ?? 'building').slice(0, 14)} · {s.encounter?.kind || 'interior'}
            </text>
          </g>
        ))}
      </svg>
    </div>
  );
}
